"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2, ChevronDown, AlertCircle } from "lucide-react";
import type { ContactFormComponent } from "../../types";
import type { RendererConfig } from "../rendererConfig";
import { RuntimeLink } from "../../runtime/RendererRuntime";

/** Turnstile explicit-render API surface (subset actually used). */
type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Single in-flight loader for the Turnstile script — many forms, one script tag. */
let turnstileLoader: Promise<TurnstileApi | null> | null = null;

function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoader) {
    turnstileLoader = new Promise((resolve) => {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
      const script = existing ?? document.createElement("script");
      const settle = () => resolve(window.turnstile ?? null);
      script.addEventListener("load", settle);
      script.addEventListener("error", () => resolve(null));
      if (!existing) {
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
  }
  return turnstileLoader;
}

type SubmitStatus = "idle" | "sending" | "success" | "error" | "turnstile-error";

const INPUT_CLASS =
  "w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm xl:text-base text-[#0F2849] placeholder:text-slate-400 outline-none transition focus:border-[#0095DB] focus:ring-2 focus:ring-[#0095DB]/25";

const LABEL_CLASS = "mb-1.5 block text-sm xl:text-base font-semibold text-[#0F2849]";

/**
 * Contact form renderer with name, email, subject and message fields.
 *
 * LIVE form: submits to the same-origin `/api/contact` worker route, which
 * verifies Cloudflare Turnstile server-side then relays to the admin API —
 * the browser never sees the admin URL or token. When the host passes
 * `config.turnstileSiteKey` the Turnstile widget renders (explicit mode) and
 * its token accompanies the submission; without a sitekey the form still
 * works (the route only skips verification when its own secret is unset).
 * A hidden honeypot field silently swallows bot submissions client-side.
 *
 * The sidebar's contact details resolve in two steps: the component's own
 * `contactInfo` (a per-page override) wins over the site-wide contact info the
 * host passes through `RendererConfig` — this is the one renderer that needs
 * `config`, which is why the registry forwards it here. When neither is set
 * the sidebar is omitted and the form takes the full width.
 */
export function ContactFormRenderer({ component, config }: { component: ContactFormComponent; config?: RendererConfig }) {
  // Editors can override the subject list per page; this default mirrors the
  // bank's standard enquiry categories ("Autre" stays last).
  const subjectOptions = component.content.subjectOptions ?? [
    "Question générale",
    "Politique monétaire",
    "Supervision financière",
    "Billets et pièces",
    "Centrales des crédits",
    "Statistiques et publications",
    "Taux de change",
    "Réglementation du change",
    "Emploi et stages",
    "Presse et médias",
    "Musée et Hôtel des Monnaies",
    "Autre",
  ];
  const contactInfo = component.content.contactInfo ?? config?.contactInfo;
  const turnstileSiteKey = config?.turnstileSiteKey;
  // Per-page preselected subject — only honoured when it exists in the list,
  // so a stale editor value degrades to the "Choisir une option" placeholder.
  const defaultSubject = component.content.defaultSubject;
  const initialSubject = defaultSubject && subjectOptions.includes(defaultSubject) ? defaultSubject : "";
  // Deux libellés que la page peut reformuler. Le formulaire ne sert pas qu'aux
  // questions : sur la page de signalement d'une infraction, « Ma question concerne »
  // décrit mal ce qu'on demande au visiteur. Non renseignés, les textes d'origine
  // restent, à la lettre.
  const subjectLabel = component.content.subjectLabel ?? "Ma question concerne";
  const messagePlaceholder = component.content.messagePlaceholder ?? "Voici ma question";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState("");
  // Honeypot — invisible to humans; a filled value flags a bot.
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");

  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string>("");

  useEffect(() => {
    if (!turnstileSiteKey) return;
    let cancelled = false;
    loadTurnstile().then((api) => {
      if (cancelled || !api || !turnstileContainerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = api.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => {
          tokenRef.current = token;
        },
        "expired-callback": () => {
          tokenRef.current = "";
        },
        "error-callback": () => {
          tokenRef.current = "";
        },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [turnstileSiteKey]);

  /** Ask Turnstile for a fresh challenge after a rejected submission. */
  const resetTurnstile = () => {
    tokenRef.current = "";
    if (widgetIdRef.current !== null) {
      window.turnstile?.reset(widgetIdRef.current);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "sending") return;

    // Honeypot tripped: pretend everything went fine, send nothing.
    if (website.trim() !== "") {
      setStatus("success");
      return;
    }

    setStatus("sending");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          subject: subject || undefined,
          message,
          // Provenance du message : le libellé éditorial de la page quand il existe, sinon
          // son chemin. La boîte de réception admin filtre « par section » sur cette valeur.
          section:
            component.content.sectionLabel ||
            (typeof window !== "undefined" ? window.location.pathname : undefined) ||
            undefined,
          turnstileToken: tokenRef.current || undefined,
        }),
      });
      if (response.ok) {
        setStatus("success");
        return;
      }
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      resetTurnstile();
      setStatus(payload?.error === "turnstile" ? "turnstile-error" : "error");
    } catch {
      resetTurnstile();
      setStatus("error");
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr] lg:items-start lg:gap-8">
      {/* Carte formulaire — l'idiome de carte de la charte (rounded-md, fond
          blanc, ring slate, AUCUNE ombre — cf. GovernorCard et les grilles). */}
      <section className="relative overflow-hidden rounded-md bg-white p-6 ring-1 ring-slate-200 sm:p-8">
        {status === "success" ? (
          <div role="status" aria-live="polite" className="flex flex-col items-center gap-4 py-8 text-center sm:py-10">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-[#0095DB]/10">
              <CheckCircle2 className="h-7 w-7 text-[#005CA9]" aria-hidden="true" />
            </span>
            <div>
              <p className="text-base xl:text-lg font-semibold text-[#005CA9]">Votre message a bien été envoyé.</p>
              <p className="mt-1.5 text-sm xl:text-base text-[#0F2849]">
                Nous vous répondrons dans les meilleurs délais.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={LABEL_CLASS}>
                  Nom et Prénom <span className="text-rose-600">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  required
                  maxLength={200}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex. Kabongo Mukendi"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="email" className={LABEL_CLASS}>
                  Adresse e-mail <span className="text-rose-600">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  maxLength={320}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nom@exemple.cd"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className={LABEL_CLASS}>
                {subjectLabel}
              </label>
              <div className="relative">
                <select
                  id="subject"
                  name="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className={`${INPUT_CLASS} appearance-none pr-10`}
                >
                  <option value="" disabled>
                    Choisir une option
                  </option>
                  {subjectOptions.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#005CA9]"
                  aria-hidden="true"
                />
              </div>
            </div>
            <div>
              <label htmlFor="message" className={LABEL_CLASS}>
                Message <span className="text-rose-600">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={8}
                required
                maxLength={8000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={messagePlaceholder}
                className={`${INPUT_CLASS} resize-y py-3`}
              />
            </div>
            {/* Honeypot : champ invisible pour un humain, rempli par les bots. */}
            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="contact-website">Site web</label>
              <input
                id="contact-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            {turnstileSiteKey ? <div ref={turnstileContainerRef} data-testid="turnstile-container" className="pt-1" /> : null}
            <div aria-live="polite" className="space-y-3">
              {status === "error" ? (
                <p
                  role="alert"
                  className="flex items-start gap-2.5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm xl:text-base font-medium text-rose-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  Une erreur est survenue, veuillez réessayer.
                </p>
              ) : null}
              {status === "turnstile-error" ? (
                <p
                  role="alert"
                  className="flex items-start gap-2.5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm xl:text-base font-medium text-rose-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  La vérification anti-spam a échoué, veuillez réessayer.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-3 border-t border-[#005CA9]/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs xl:text-sm text-slate-500">
                <span className="text-rose-600">*</span> Mentions obligatoires
              </span>
              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-[#005CA9] px-6 py-2.5 text-sm xl:text-base font-semibold text-white transition hover:bg-[#0F2849] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
              >
                {status === "sending" ? "Envoi en cours…" : "J’envoie"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </form>
        )}
      </section>
      {contactInfo ? (
        /* Carte coordonnées — même carte blanche de la charte que le reste du
           site ; chaque ligne ne se rend que si sa donnée existe (le téléphone
           a été retiré du contact officiel). */
        <aside className="rounded-md bg-white p-6 ring-1 ring-slate-200 sm:p-7">
          <h2 className="text-base xl:text-lg font-semibold text-[#005CA9]">Coordonnées</h2>
          <div className="mt-2 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
          <ul className="mt-5 space-y-5">
            {contactInfo.address?.trim() ? (
              <li className="flex items-start gap-3.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F4F8FC] text-[#005CA9] ring-1 ring-slate-200">
                  <MapPin className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#005CA9]">Adresse</p>
                  <p className="mt-0.5 text-sm xl:text-base leading-relaxed text-[#0F2849]">{contactInfo.address}</p>
                </div>
              </li>
            ) : null}
            {contactInfo.phone?.trim() ? (
              <li className="flex items-start gap-3.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F4F8FC] text-[#005CA9] ring-1 ring-slate-200">
                  <Phone className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#005CA9]">Téléphone</p>
                  <RuntimeLink
                    href={`tel:${contactInfo.phone.replace(/\s+/g, "")}`}
                    className="mt-0.5 inline-block text-sm xl:text-base font-medium text-[#0F2849] underline-offset-2 transition hover:text-[#005CA9] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 rounded-sm"
                  >
                    {contactInfo.phone}
                  </RuntimeLink>
                </div>
              </li>
            ) : null}
            {contactInfo.email?.trim() ? (
              <li className="flex items-start gap-3.5">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F4F8FC] text-[#005CA9] ring-1 ring-slate-200">
                  <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#005CA9]">E-mail</p>
                  <RuntimeLink
                    href={`mailto:${contactInfo.email}`}
                    className="mt-0.5 inline-block break-all text-sm xl:text-base font-medium text-[#0F2849] underline-offset-2 transition hover:text-[#005CA9] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 rounded-sm"
                  >
                    {contactInfo.email}
                  </RuntimeLink>
                </div>
              </li>
            ) : null}
          </ul>
        </aside>
      ) : null}
    </div>
  );
}
